import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import { setPageTitle } from 'common/hooks/usePageTitle';

import { history } from 'common/helpers';
import MoreOptions from 'common/components/organisms/MoreOptions';
import AccountMenuItems from 'common/components/organisms/AccountMenuItems';
import SearchFilters from 'common/components/atoms/SearchFilters';
import FilterItem_T from 'common/components/atoms/FilterItem';
import Filter from 'common/components/molecules/Filter';
import FilterCountContainer from 'common/components/atoms/FilterCountContainer';
import FlexContainer from 'common/components/atoms/FlexContainer';
import List from 'common/components/molecules/List';
import MsgContainer from 'common/components/atoms/MsgContainer';
import Checkbox from 'common/components/atoms/Checkbox';
import PlusSvg from 'common/images/PlusSvg';
import { isFilterApplied } from 'common/utils';
import Button, { ButtonTransparent } from 'common/components/atoms/Button';
import { operations, mapComponentToResource } from 'common/rbac/constants';
import RBAC from 'common/rbac/RBAC';

import FirmwareDeploymentItem from './FirmwareDeploymentItem';
import AddDeploymentModal from './AddDeploymentModal';

import * as sensorActions from '../../Sensors/actions/sensors.actions';
import * as firmwareDeploymentActions from '../actions/firmwareDeployment.actions';

const ListContainer = styled(FlexContainer).attrs({ direction: 'column' })`
position: relative;
overflow: auto;
width: ${props => props.width};
height: 100%;
${props =>
    props.width === '70%' &&
  `border-right: 2px solid ${props.theme.colors.greyL};`}
${props =>
    props.minWidth && `min-width: ${props.minWidth};`}
`;

const DeploymentsContainer = styled(FlexContainer).attrs({ direction: 'row' })`
  background-color: ${props => props.theme.colors.white};
  overflow: auto;
`;

const FilterItem = styled(FilterItem_T)`
  padding: 0.5em 1em;
  min-width: ${props => props.minWidth || '150px'};
  color: ${props => props.theme.colors.lightGray};
  span {
    margin-left: 0.5em;
  }
  ${props => props.paddingBottom && 'padding-bottom: 15px;'}
`;

class FirmwareDeployment extends Component {
  constructor(props) {
    super(props);
    this.initialSearchParams = {
      firmware_type: [],
      association_type: [],
      target_firmware_type: [],
      account_search_key: '',
      accounts: [],
    };
    this.state = {
      allAccounts: [],
      searchParams: {
        ...this.initialSearchParams,
      },
      filtersOpen: {
        firmware_type: false,
        association_type: false,
        target_firmware_type: false,
      },
      targetFirmwareOptions: [],
      addDeploymentModalOpen: false,
    };
  }

  getAssociationOptions = () => ([
    { text: this.props.t('firmwareDeployment.associationType.accounts', { defaultValue: 'Accounts' }), value: 'accounts' },
    { text: this.props.t('firmwareDeployment.associationType.devices', { defaultValue: 'Devices' }), value: 'devices' },
    { text: this.props.t('firmwareDeployment.associationType.firmwares', { defaultValue: 'Firmwares' }), value: 'firmwares' },
  ]);

  getFirmwareOptions = () => ([
    { text: this.props.t('firmwareDeployment.firmwareType.mcu', { defaultValue: 'MCU' }), value: 'mcu' },
    { text: this.props.t('firmwareDeployment.firmwareType.wifi', { defaultValue: 'WiFi' }), value: 'wifi' },
    { text: this.props.t('firmwareDeployment.firmwareType.ethernet', { defaultValue: 'Ethernet' }), value: 'ethernet' },
    { text: this.props.t('firmwareDeployment.firmwareType.bluetooth', { defaultValue: 'Bluetooth' }), value: 'bluetooth' },
    { text: this.props.t('firmwareDeployment.firmwareType.bootloader', { defaultValue: 'Bootloader' }), value: 'bootloader' },
  ]);

  componentDidMount() {
    const title = this.props.title;
    setPageTitle({
      partnerTitle: title,
      pageTitle: this.props.t('pageTitles.firmwareDeployment', { defaultValue: 'Firmware Deployment' })
    });

    const { getResourcesBasedOnType } = this.props.sensorActions;
    const {
      setFirmwareDeploymentFilters,
      getAllOpsTx,
      getAllOpsMotes,
      getFirmwares
    } = this.props.firmwareDeploymentActions;

    getResourcesBasedOnType('mcu_firmware').then((res) => {
      this.setState({ targetFirmwareOptions: res });
    });
    const filters = [
      { name: 'account_id', op: 'in', value: [] },
      { name: 'firmware_type', op: 'in', value: [] },
      { name: 'association_type', op: 'in', value: [] },
      { name: 'target_version', op: 'in', value: [] },
    ];
    setFirmwareDeploymentFilters(filters);
    getAllOpsTx();
    getAllOpsMotes();
    getFirmwares({ all: true });
  }

  renderFilterOptions = (options, name) => {
    if (_.isEmpty(options)) {
      return (
        <MsgContainer>
          <span>{this.props.t('common.noneAvailable', { defaultValue: 'No {{name}} are available', name })}</span>
        </MsgContainer>
      );
    }

    return options.map((option, index) => {
      const selected = _.includes(this.state.searchParams[name], option.value);
      return (
        <FilterItem
          key={index.toString()}
          className="dropdown-item"
          selected={selected}
          onClick={() => {
            let types = [];
            if (selected) {
              types = _.filter(
                this.state.searchParams[name],
                t => t !== option.value
              );
            } else {
              types = [...this.state.searchParams[name], option.value];
            }
            this.changeFilter(name, types, selected);
          }}
        >
          <Checkbox value={selected} />
          <span>{option.text}</span>
        </FilterItem>
      );
    });
  };

  toggleFilterOpen = (filter) => {
    this.setState(prevState => ({
      filtersOpen: {
        ...prevState.filtersOpen,
        [filter]: !prevState.filtersOpen[filter]
      }
    }));
  };

  toggleAddDeploymentModal = () => {
    this.setState(prevState => ({ addDeploymentModalOpen: !prevState.addDeploymentModalOpen }));
  }

  changeFilter = (filter, value) => {
    const searchParams = {
      ...this.state.searchParams,
      [filter]: value
    };
    this.setState({ searchParams });
    return this.search(searchParams);
  };

  search = (params = null) => {
    const { searchParams: stateParams } = this.state;
    const searchParams = params || stateParams;
    const { setFirmwareDeploymentFilters } = this.props.firmwareDeploymentActions;

    const filters = [
      { name: 'account_id', op: 'in', value: searchParams.accounts },
      { name: 'firmware_type', op: 'in', value: searchParams.firmware_type || [] },
      { name: 'association_type', op: 'in', value: searchParams.association_type || [] },
      { name: 'target_version', op: 'in', value: searchParams.target_firmware_type || [] },
    ];
    return setFirmwareDeploymentFilters(filters);
  };

  clearAllFilters = () => {
    const searchParams = {
      ...this.initialSearchParams
    };
    this.setState({ searchParams });
    this.search(searchParams);
  }

  searchAccountFilter = (value) => {
    this.setState(prevState => ({
      accounts: prevState.allAccounts.filter(u =>
        u.text.toLowerCase().includes(value.toLowerCase())
      ),
      searchParams: {
        ...prevState.searchParams,
        account_search_key: value
      }
    }));
  };

  addDeployment = (device_type, fw_id, name, association_type, associations) => {
    let paramsList = [];
    const { user } = this.props;
    const { addFirmwareDeployment } = this.props.firmwareDeploymentActions;
    switch (association_type) {
      case 'devices':
        paramsList = [{
          account_id: user.account_id,
          device_type,
          fw_id,
          name,
          association_type,
          device_ids: associations
        }];
        break;
      case 'firmwares':
        paramsList = [{
          account_id: user.account_id,
          device_type,
          fw_id,
          name,
          association_type,
          firmware_ids: associations
        }];
        break;
      case 'accounts':
        paramsList = associations.map(account_id => ({
          association_type,
          device_ids: [],
          device_type,
          firmware_ids: [],
          fw_id,
          name,
          account_id
        }));
        break;
      default:
    }
    paramsList.map(params => addFirmwareDeployment(params));

    this.toggleAddDeploymentModal();
  }

  selectGivenItems = (selected, key) => {
    this.setState(
      prevState => ({
        searchParams: {
          ...prevState.searchParams,
          [key]: selected
        },
        filtersOpen: {
          ...prevState.filtersOpen,
          [key]: false
        }
      }),
      this.search
    );
  };

  onClickDeployment = (deployment_id) => {
    history.push({
      pathname: `/admin-dashboard/firmware-deployments/${deployment_id}`,
      state: { from_app: true }
    });
  }

  render() {
    const associationOptions = this.getAssociationOptions();
    const firmwareOptions = this.getFirmwareOptions();
    const {
      deployments: items,
      user,
      metadata
    } = this.props;
    const { loadMoreFirmwareDeployments } = this.props.firmwareDeploymentActions;
    const props = {
      columnSizes: [16, 20, 16, 16, 20, 16],
      headers: [
        {
          name: 'id',
          label: this.props.t('firmwareDeployments.headers.deploymentId', { defaultValue: 'Deployment ID' }),
        },
        {
          name: 'name',
          label: this.props.t('common.name', { defaultValue: 'Name' }),
        },
        {
          name: 'firmware_type',
          label: this.props.t('firmwareDeployments.headers.firmwareType', { defaultValue: 'Firmware Type' })
        },
        {
          name: 'association_type',
          label: this.props.t('firmwareDeployments.headers.associationType', { defaultValue: 'Association Type' })
        },
        {
          name: 'target_version',
          label: this.props.t('firmwareDeployments.headers.targetVersion', { defaultValue: 'Target Version' })
        },
        {
          name: 'account_name',
          label: this.props.t('firmwareDeployments.headers.accountName', { defaultValue: 'Account Name' })
        }
      ],
      maxHeightAfterScroll: 'calc(100vh - var(--title-bar-offset, 0px) - 160px)',
      triggerLoadMore: 'calc(100vh - var(--title-bar-offset, 0px) - 195px)',
      emptyMsg: this.props.t('common.noResultsFound', { defaultValue: 'No results found' }),
      ItemComponent: FirmwareDeploymentItem,
      onClick: this.onClickDeployment,
      items: { ...items, loading: items.loading || metadata.loading },
      loadMoreItems: loadMoreFirmwareDeployments,
      user
    };

    const getFilterText = (options, type) => {
      let typeText = '';
      if (!_.isEmpty(options)) {
        typeText = type.reduce((acc, id, idx) => {
          const name = options.filter(type => type.value === id);
          if (idx === 0) return name[0].text;
          if (idx === 1) return `${acc}, ${name[0].text}...`;
          return acc;
        }, '');
      }
      return typeText;
    };

    return (
      <div>
      <MoreOptions>
        <AccountMenuItems />
      </MoreOptions>
      <SearchFilters height="60px">
        {items && (
            <FilterCountContainer>{items.total_count}</FilterCountContainer>
        )}
        {this.props.adminDashboard.adminOpen ?
          (
            <RBAC
              resource={mapComponentToResource.FirmwareDeployments}
              operation={operations.Create}
              yes={(
            <ButtonTransparent title={this.props.t('firmwareDeployments.actions.addDevice', { defaultValue: 'Add Device' })} onClick={this.toggleAddDeploymentModal} secondary="#000" marginLeft="10px">
              <PlusSvg width={16} height={16} />
            </ButtonTransparent>
              )}
            />
          ) : null
          }
        <Filter
          title={this.props.t('firmwareDeployments.filters.firmwareType', { defaultValue: 'Firmware Type:' })}
          value={getFilterText(firmwareOptions, this.state.searchParams.firmware_type)}
          open={this.state.filtersOpen.firmware_type}
          toggleOpen={() => this.toggleFilterOpen('firmware_type')}
        >
            {this.renderFilterOptions(firmwareOptions, 'firmware_type')}
        </Filter>
        <Filter
          title={this.props.t('firmwareDeployments.filters.associationType', { defaultValue: 'Association Type:' })}
          value={getFilterText(associationOptions, this.state.searchParams.association_type)}
          open={this.state.filtersOpen.association_type}
          toggleOpen={() => this.toggleFilterOpen('association_type')}
        >
            {this.renderFilterOptions(associationOptions, 'association_type')}
        </Filter>
        <Filter
          title={this.props.t('firmwareDeployments.filters.targetFirmwareVersion', { defaultValue: 'Target Firmware Version:' })}
          value={getFilterText(this.state.targetFirmwareOptions, this.state.searchParams.target_firmware_type)}
          open={this.state.filtersOpen.target_firmware_type}
          toggleOpen={() => this.toggleFilterOpen('target_firmware_type')}
        >
            {this.renderFilterOptions(this.state.targetFirmwareOptions, 'target_firmware_type')}
        </Filter>
        {isFilterApplied(this.state.searchParams) && (
            <Button text onClick={this.clearAllFilters} fontSize="11px" margin="0 20px">
              {this.props.t('common.clearFilters', { defaultValue: 'Clear Filters' })}
            </Button>
        )}
      </SearchFilters>
      <DeploymentsContainer>
          <ListContainer
            width="100%"
            minWidth="45rem"
          >
            <List
              {...props}
            />
          </ListContainer>
      </DeploymentsContainer>
      {this.state.addDeploymentModalOpen && (
        <AddDeploymentModal
          close={this.toggleAddDeploymentModal}
        />
      )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  deployments: state.firmwareDeployments.deployments,
  metadata: state.firmwareDeployments.metadata,
  adminDashboard: state.adminDashboard,
  user: state.user,
  title: state.companyReducer.partner.title
});

const mapDispatchToProps = dispatch => ({
  firmwareDeploymentActions: bindActionCreators(firmwareDeploymentActions, dispatch),
  sensorActions: bindActionCreators(sensorActions, dispatch)
});


export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(FirmwareDeployment));
